import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, UserPlus, X, TrendingUp, Coins, Clock, Wallet, Cpu, Dice5, Ticket, Receipt, Radio, Printer, ShieldAlert } from 'lucide-react';
import PendingPayoutsPanel from './PendingPayoutsPanel';
import { BuildStamp } from '../../components/BuildStamp';
import FinanceSummaryPanel from './FinanceSummaryPanel';
import CustomerPanel from '../../components/admin/CustomerPanel';
import CashierDigestPreviewPanel from './CashierDigestPreviewPanel';
import EndOfDaySheet from '../../components/admin/EndOfDaySheet';
import SportsExposureQuickView from '../../components/admin/SportsExposureQuickView';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const RECENT_KEY = 'admin_recent_credit_customers';
const RECENT_MAX = 5;

const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmt$compact = (n) => {
    const v = Number(n || 0);
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    if (abs >= 10000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
    return `${sign}$${abs.toFixed(2)}`;
};

const readRecent = () => {
    try {
        const raw = localStorage.getItem(RECENT_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
    } catch {
        return [];
    }
};

const pushRecent = (u) => {
    if (!u?.id) return readRecent();
    try {
        const prev = readRecent().filter((r) => r.id !== u.id);
        const next = [
            { id: u.id, name: u.name || u.email || '—', email: u.email || '' },
            ...prev,
        ].slice(0, RECENT_MAX);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
        return next;
    } catch {
        return readRecent();
    }
};

/**
 * Streamlined admin home screen (Feb 2026 declutter).
 * Surfaces only what an operator needs at a glance:
 *   • Today snapshot chips (Net + Slots Sink)
 *   • Pending Approvals — new signups awaiting review
 *   • Quick Add Credits — name search + recent-customer chips
 *   • Cashout Requests — pending payouts queue
 */
const HomeDashboard = ({ users = [], onOpenCredit, onOpenFull, onDrilldown, isHome = true }) => {
    const [q, setQ] = useState('');
    const [focused, setFocused] = useState(false);
    const [recent, setRecent] = useState(() => readRecent());
    const [panelUser, setPanelUser] = useState(null);
    const [snapshot, setSnapshot] = useState({ net: null, agentScore: null, agentLow: false, owed: null, owedCount: 0, loading: true });
    const [showEod, setShowEod] = useState(false);
    const [showExposure, setShowExposure] = useState(false);
    const inputRef = useRef(null);
    const wrapRef = useRef(null);

    const usersById = useMemo(() => {
        const m = new Map();
        users.forEach((u) => { if (u?.id) m.set(u.id, u); });
        return m;
    }, [users]);

    // Filter to real customers only. Match on name / email / phone.
    const matches = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return [];
        return users
            .filter((u) => u.role === 'user')
            .filter((u) => (
                (u.name || '').toLowerCase().includes(term)
                || (u.email || '').toLowerCase().includes(term)
                || (u.phone || '').toLowerCase().includes(term)
            ))
            .slice(0, 8);
    }, [q, users]);

    // Close dropdown when tapping outside the search block.
    useEffect(() => {
        const onDocDown = (e) => {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) setFocused(false);
        };
        document.addEventListener('mousedown', onDocDown);
        document.addEventListener('touchstart', onDocDown);
        return () => {
            document.removeEventListener('mousedown', onDocDown);
            document.removeEventListener('touchstart', onDocDown);
        };
    }, []);

    const loadSnapshot = useCallback(async () => {
        try {
            const [fin, agent, payouts, agentStatus, feeWeek, summ, splitRes] = await Promise.allSettled([
                axios.get(`${API}/admin/reports/financial`, { withCredentials: true }),
                axios.get(`${API}/admin/majik/agent-balance`, { withCredentials: true }),
                axios.get(`${API}/admin/pending-payouts`, { withCredentials: true }),
                axios.get(`${API}/admin/casino-agent-status`, { withCredentials: true }),
                axios.get(`${API}/admin/cashout-fees/weekly-summary`, { withCredentials: true }),
                axios.get(`${API}/admin/finance/summary`, { withCredentials: true }),
                axios.get(`${API}/staff/credits-total`, { withCredentials: true }),
            ]);
            const today = fin.status === 'fulfilled' ? (fin.value.data.today || {}) : {};
            // Owner rule (Nov 2026): today's figures = loads today minus cash-outs asked for AND paid today;
            // payments on older cash-outs are shown apart as "old balance".
            const split = splitRes.status === 'fulfilled' ? splitRes.value.data : null;
            const loaded = split ? split.loads_today : (today?.deposits?.total ?? today?.loaded_in?.total ?? 0);
            const redeemed = split ? split.cashouts_today : (today?.withdrawals?.total ?? 0);
            const net = Number(loaded) - Number(redeemed);
            const oldPaid = split ? Number(split.old_cashouts_paid || 0) : 0;
            const oldPaidCount = split ? Number(split.old_cashouts_count || 0) : 0;
            // Game results today: the Money summary's own figure (finance_metrics, 7 AM–7 AM Central business day)
            const sd = summ.status === 'fulfilled' ? summ.value.data : null;
            const houseProfit = sd ? Number(sd.game_results_total || 0) : null;
            const feesCollectedToday = sd ? Number(sd.fees_retained?.total || 0) : 0;
            const casinoProfit = Number(today?.casino?.profit || 0);
            const agentScore = agent.status === 'fulfilled'
                ? Number(agent.value.data?.score || 0)
                : null;
            const agentLow = agentStatus.status === 'fulfilled'
                ? !!agentStatus.value.data?.active
                : false;
            let owed = null;
            let owedCount = 0;
            if (payouts.status === 'fulfilled') {
                const rows = Array.isArray(payouts.value.data) ? payouts.value.data : [];
                owed = rows.reduce((s, r) => s + (r.remaining_to_pay != null ? Number(r.remaining_to_pay) : Math.abs(Number(r.amount) || 0)), 0);  // net still to send, same as Pending Payouts
                owedCount = rows.length;
            }
            // At-a-glance today totals — new Feb 2026 tiles so the operator
            // sees where the day's money is flowing without opening drilldowns.
            const slotsIn = Number(today?.casino?.topup_total || 0);
            const tablesWagered =
                Number(today?.blackjack?.wagered || 0) +
                Number(today?.craps?.wagered || 0) +
                Number(today?.three_card_poker?.wagered || 0) +
                Number(today?.roulette?.wagered || 0);
            const lottoWagered = Number(today?.lottery?.wagered || 0);
            // Cashout-fee weekly totals (Feb 2026) — tiny operator visibility
            // into the rebate liability building each week.
            const feesCollected = feeWeek.status === 'fulfilled' ? Number(feeWeek.value.data?.fees_collected || 0) : 0;
            const feeCashouts = feeWeek.status === 'fulfilled' ? Number(feeWeek.value.data?.cashout_count || 0) : 0;
            setSnapshot({ net, moneyIn: Number(loaded) || 0, moneyOut: Number(redeemed) || 0, oldPaid, oldPaidCount, houseProfit, casinoProfit, feesCollectedToday, agentScore, agentLow, owed, owedCount, slotsIn, tablesWagered, lottoWagered, feesCollected, feeCashouts, todayRaw: today, loading: false });
        } catch {
            setSnapshot((s) => ({ ...s, loading: false }));
        }
    }, []);

    useEffect(() => {
        loadSnapshot();
        const id = setInterval(loadSnapshot, 60_000);
        return () => clearInterval(id);
    }, [loadSnapshot]);

    const pickUser = (u) => {
        setFocused(false);
        setQ('');
        setRecent(pushRecent(u));
        // Single click → open the full Customer panel (info + edit + actions).
        setPanelUser(u);
    };

    const pickRecent = (r) => {
        // Prefer the fresh user from state (up-to-date credits); fall back
        // to the stored snapshot if they've been paginated out.
        const u = usersById.get(r.id) || { id: r.id, name: r.name, email: r.email };
        pickUser(u);
    };

    const dropRecent = (e, id) => {
        e.stopPropagation();
        const next = recent.filter((r) => r.id !== id);
        setRecent(next);
        try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    };

    const netColor = (v) => {
        if (v == null) return '#A0AAB2';
        return v >= 0 ? '#39FF14' : '#FF3B30';
    };

    return (
        <div className="mb-6 space-y-4">
            {/* ── Site-wide Money In / Money Out header (real-time) ──
                In  = real money loaded (PayPal + cashier/kiosk credit loads)
                Out = cashouts / redemptions paid.
                The big at-a-glance number; tap any tile to drill into detail. */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="home-money-header">
                <button
                    type="button"
                    onClick={() => onDrilldown?.('money-in')}
                    className="rounded-2xl border p-4 text-left hover:brightness-110 active:scale-[0.99] transition"
                    style={{ borderColor: '#39FF1444', background: 'rgba(57,255,20,0.07)' }}
                    data-testid="home-money-in"
                    title="Total real money loaded today (PayPal + cashier/kiosk). Tap for detail."
                >
                    <p className="text-[10px] sm:text-xs font-bold tracking-wider text-[#A0AAB2]">MONEY IN · TODAY</p>
                    <p className="mt-1 text-2xl sm:text-4xl font-black tabular-nums text-[#39FF14] leading-none">
                        {snapshot.loading ? '…' : fmt$(snapshot.moneyIn)}
                    </p>
                    <p className="mt-1 text-[10px] text-[#5f6b73]">real loads</p>
                </button>
                <button
                    type="button"
                    onClick={() => onDrilldown?.('money-out')}
                    className="rounded-2xl border p-4 text-left hover:brightness-110 active:scale-[0.99] transition"
                    style={{ borderColor: '#FF6B0044', background: 'rgba(255,107,0,0.07)' }}
                    data-testid="home-money-out"
                    title="Total cashouts / redemptions paid out today. Tap for detail."
                >
                    <p className="text-[10px] sm:text-xs font-bold tracking-wider text-[#A0AAB2]">MONEY OUT · TODAY</p>
                    <p className="mt-1 text-2xl sm:text-4xl font-black tabular-nums text-[#FF6B00] leading-none">
                        {snapshot.loading ? '…' : fmt$(snapshot.moneyOut)}
                    </p>
                    <p className="mt-1 text-[10px] text-[#5f6b73]">asked for &amp; paid today</p>
                    {snapshot.oldPaid > 0 && (
                        <p className="mt-0.5 text-[10px] font-bold text-amber-300" data-testid="home-old-paid">
                            + {fmt$(snapshot.oldPaid)} old cash-out{snapshot.oldPaidCount === 1 ? '' : 's'} paid (not in today)
                        </p>
                    )}
                </button>
                <button
                    type="button"
                    onClick={() => onDrilldown?.('today-net')}
                    className="rounded-2xl border p-4 text-left hover:brightness-110 active:scale-[0.99] transition"
                    style={{ borderColor: `${netColor(snapshot.net)}44`, background: `${netColor(snapshot.net)}12` }}
                    data-testid="home-money-net"
                    title="Net cash flow today (Money In − Money Out). Tap to open full reports."
                >
                    <p className="text-[10px] sm:text-xs font-bold tracking-wider text-[#A0AAB2]">NET · TODAY</p>
                    <p className="mt-1 text-2xl sm:text-4xl font-black tabular-nums leading-none" style={{ color: netColor(snapshot.net) }}>
                        {snapshot.loading || snapshot.net == null ? '…' : `${snapshot.net >= 0 ? '+' : '-'}${fmt$(Math.abs(snapshot.net))}`}
                    </p>
                    <p className="mt-1 text-[10px] text-[#5f6b73]">today's profit · loads − today's cash-outs</p>
                </button>
            </div>
            <div className="-mt-2 flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[10px] text-[#5f6b73]" data-testid="home-money-note">
                    Central Time · live now (auto-refreshes) · business day runs 7am → 7am CT
                </p>
                <button
                    type="button"
                    onClick={() => setShowEod(true)}
                    className="flex items-center gap-1.5 rounded-full border border-[#FFD700]/40 bg-[#FFD700]/10 hover:bg-[#FFD700]/20 active:scale-[0.98] transition px-3 py-1.5"
                    data-testid="home-print-eod-btn"
                    title="One-tap printable 7am→7am accounting summary"
                >
                    <Printer className="w-3.5 h-3.5 text-[#FFD700]" />
                    <span className="text-[10px] font-black tracking-wider text-[#FFD700]">PRINT END-OF-DAY</span>
                </button>
            </div>

            {showEod && <EndOfDaySheet onClose={() => setShowEod(false)} initialToday={snapshot.todayRaw || null} />}

            {/* Quick Add Credits — search a player by name and open the
                load-credits sheet in one tap. Recent customers pinned as chips
                under the search box for one-tap re-open. */}
            <div
                ref={wrapRef}
                className="rounded-2xl border border-white/10 bg-[#0e0e0e] p-5"
                data-testid="home-quick-add-credits"
            >
                <div className="flex items-center gap-2 mb-3">
                    <UserPlus className="w-5 h-5 text-[#FFD700]" />
                    <p className="text-xs uppercase tracking-wider text-[#A0AAB2] font-bold">
                        Find Customer
                    </p>
                </div>

                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AAB2] pointer-events-none" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={q}
                        onFocus={() => setFocused(true)}
                        onChange={(e) => { setQ(e.target.value); setFocused(true); }}
                        placeholder="Type a name, email, or phone…"
                        className="w-full pl-10 pr-10 py-3 rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/30 text-base"
                        data-testid="home-customer-search"
                        autoComplete="off"
                    />
                    {q && (
                        <button
                            type="button"
                            onClick={() => { setQ(''); inputRef.current?.focus(); }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-[#A0AAB2] hover:text-white hover:bg-white/10"
                            aria-label="Clear search"
                            data-testid="home-customer-search-clear"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}

                    {/* Results dropdown */}
                    {focused && q.trim() && (
                        <div
                            className="absolute z-40 left-0 right-0 mt-2 rounded-xl border border-white/10 bg-[#0a0a0a] shadow-2xl overflow-hidden"
                            data-testid="home-customer-search-results"
                        >
                            {matches.length === 0 ? (
                                <div className="px-4 py-4 text-sm text-[#A0AAB2] text-center">
                                    No customers match &ldquo;{q.trim()}&rdquo;.
                                </div>
                            ) : (
                                matches.map((u) => (
                                    <button
                                        type="button"
                                        key={u.id}
                                        onClick={() => pickUser(u)}
                                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/5 border-b border-white/5 last:border-b-0 transition-colors"
                                        data-testid={`home-customer-search-hit-${u.id}`}
                                    >
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-white truncate">
                                                {u.name || u.email || '—'}
                                            </p>
                                            <p className="text-[11px] text-[#A0AAB2] truncate">
                                                {u.email}
                                                {u.phone ? ` · ${u.phone}` : ''}
                                            </p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Credits</p>
                                            <p className="text-sm font-black text-[#39FF14] tabular-nums">
                                                {fmt$(u.credits)}
                                            </p>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* Recent customers — last 5 credit recipients as tap-chips. */}
                {recent.length > 0 && (
                    <div className="mt-3" data-testid="home-recent-customers">
                        <div className="flex items-center gap-1.5 mb-1.5">
                            <Clock className="w-3 h-3 text-[#A0AAB2]" />
                            <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">Recent</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {recent.map((r) => (
                                <button
                                    type="button"
                                    key={r.id}
                                    onClick={() => pickRecent(r)}
                                    className="group inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full border border-[#FFD700]/30 bg-[#FFD700]/10 text-white hover:bg-[#FFD700]/20 transition-colors"
                                    data-testid={`home-recent-chip-${r.id}`}
                                    title={r.email || r.name}
                                >
                                    <span className="text-xs font-semibold truncate max-w-[140px]">
                                        {r.name}
                                    </span>
                                    <span
                                        role="button"
                                        tabIndex={-1}
                                        onClick={(e) => dropRecent(e, r.id)}
                                        className="w-4 h-4 rounded-full flex items-center justify-center text-[#A0AAB2] hover:text-white hover:bg-white/10"
                                        aria-label={`Remove ${r.name} from recent`}
                                        data-testid={`home-recent-chip-remove-${r.id}`}
                                    >
                                        <X className="w-3 h-3" />
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <p className="mt-3 text-[11px] text-[#A0AAB2]">
                    Tap a customer to open their full record — info, edit, credits & actions in one place.
                </p>
            </div>


            {/* Quick actions — Floor View front-and-center. */}
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() => onDrilldown?.('floor-live')}
                    className="flex-1 min-w-[160px] rounded-xl border border-[#FFD700]/40 bg-[#FFD700]/10 hover:bg-[#FFD700]/20 active:scale-[0.99] transition px-4 py-3 text-left"
                    data-testid="home-floor-view-btn"
                >
                    <div className="flex items-center gap-2">
                        <Radio className="w-4 h-4 text-[#FFD700]" />
                        <span className="font-black tracking-wide text-[#FFD700]">FLOOR VIEW</span>
                    </div>
                    <p className="text-[10px] text-[#A0AAB2] mt-0.5">See who's playing what, live — every game</p>
                </button>
                <button
                    type="button"
                    onClick={() => setShowExposure(true)}
                    className="flex-1 min-w-[160px] rounded-xl border border-[#ff5470]/40 bg-[#ff5470]/10 hover:bg-[#ff5470]/20 active:scale-[0.99] transition px-4 py-3 text-left"
                    data-testid="home-sports-exposure-btn"
                    title="Sportsbook exposure — top 3 riskiest games right now"
                >
                    <div className="flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4 text-[#ff5470]" />
                        <span className="font-black tracking-wide text-[#ff5470]">EXPOSURE</span>
                    </div>
                    <p className="text-[10px] text-[#A0AAB2] mt-0.5">Sportsbook liability — riskiest games now</p>
                </button>
            </div>

            {showExposure && <SportsExposureQuickView onClose={() => setShowExposure(false)} />}

            {/* Today snapshot chips — compact peek at Net + Slots Sink so the
                operator sees the daily pulse before any queue action. Tap
                either to drill down to the relevant report. */}
            <div className="flex flex-wrap gap-2" data-testid="home-today-chips">
                <button
                    type="button"
                    onClick={() => onDrilldown?.('today-net')}
                    className="flex items-center gap-2 rounded-full border px-3 py-1.5 hover:brightness-110 active:scale-[0.98] transition"
                    style={{ borderColor: `${netColor(snapshot.houseProfit)}55`, background: `${netColor(snapshot.houseProfit)}12` }}
                    data-testid="home-today-net"
                    title={`Game results today: same figure as Money summary (in-house games, 7 AM–7 AM Central, before promotions and costs, not profit). Not included: cashout fees kept ${fmt$compact(snapshot.feesCollectedToday || 0)} and Majik casino transfers ${fmt$compact(snapshot.casinoProfit || 0)} (credits sent minus returned; the game result is on Majik's side). Cash flow (received − paid) = ${snapshot.net >= 0 ? '+' : ''}${fmt$compact(snapshot.net || 0)}.`}
                >
                    <TrendingUp className="w-3.5 h-3.5" style={{ color: netColor(snapshot.houseProfit) }} />
                    <span className="text-[10px] font-bold tracking-wider text-[#A0AAB2]" data-testid="home-today-net-label">TODAY GAME RESULTS</span>
                    <span
                        className="text-sm font-black tabular-nums"
                        style={{ color: netColor(snapshot.houseProfit) }}
                        data-testid="home-today-profit-value"
                    >
                        {snapshot.loading || snapshot.houseProfit == null
                            ? '…'
                            : `${snapshot.houseProfit >= 0 ? '+' : ''}${fmt$compact(snapshot.houseProfit)}`}
                    </span>
                    <span className="text-[9px] text-[#666] tabular-nums" data-testid="home-today-flow">
                        flow {snapshot.net >= 0 ? '+' : ''}{fmt$compact(snapshot.net || 0)}
                    </span>
                </button>
                <button
                    type="button"
                    onClick={() => onDrilldown?.('agent-score')}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1.5 hover:brightness-110 active:scale-[0.98] transition ${snapshot.agentLow ? 'animate-pulse' : ''}`}
                    style={snapshot.agentLow
                        ? { borderColor: '#FF3B3080', background: 'rgba(255,59,48,0.14)' }
                        : { borderColor: '#FFD70055', background: 'rgba(255,215,0,0.08)' }}
                    data-testid="home-agent-score"
                    data-agent-low={snapshot.agentLow ? '1' : '0'}
                    title={snapshot.agentLow
                        ? 'Agent bankroll is below the low-agent threshold — top up before redeems start bouncing.'
                        : 'MajikBonus cashier agent bankroll. Tap to open Slots Sync.'}
                >
                    <Coins className={`w-3.5 h-3.5 ${snapshot.agentLow ? 'text-[#FF3B30]' : 'text-[#FFD700]'}`} />
                    <span className="text-[10px] font-bold tracking-wider text-[#A0AAB2]">AGENT SCORE</span>
                    <span
                        className="text-sm font-black tabular-nums"
                        style={{ color: snapshot.agentLow ? '#FF3B30' : '#FFD700' }}
                    >
                        {snapshot.loading || snapshot.agentScore == null
                            ? '…'
                            : fmt$compact(snapshot.agentScore)}
                    </span>
                    {snapshot.agentLow && (
                        <span className="text-[9px] font-black uppercase tracking-widest text-[#FF3B30] bg-white/90 rounded-sm px-1 py-0.5 leading-none">
                            LOW
                        </span>
                    )}
                </button>
                <button
                    type="button"
                    onClick={() => onDrilldown?.('owed')}
                    className="flex items-center gap-2 rounded-full border px-3 py-1.5 hover:brightness-110 active:scale-[0.98] transition"
                    style={{ borderColor: '#FF6B0055', background: 'rgba(255,107,0,0.10)' }}
                    data-testid="home-owed"
                    title={`${snapshot.owedCount || 0} cashout${snapshot.owedCount === 1 ? '' : 's'} awaiting payment. Tap to open the queue.`}
                >
                    <Wallet className="w-3.5 h-3.5 text-[#FF6B00]" />
                    <span className="text-[10px] font-bold tracking-wider text-[#A0AAB2]">OWED</span>
                    <span className="text-sm font-black tabular-nums text-[#FF6B00]">
                        {snapshot.loading || snapshot.owed == null ? '…' : fmt$compact(snapshot.owed)}
                    </span>
                    {snapshot.owedCount > 0 && (
                        <span className="text-[10px] font-bold text-[#A0AAB2]">
                            · {snapshot.owedCount}
                        </span>
                    )}
                </button>

                {/* Slots transferred TODAY — MajikBonus topups from wallet → casino */}
                <button
                    type="button"
                    onClick={() => onDrilldown?.('slots')}
                    className="flex items-center gap-2 rounded-full border px-3 py-1.5 hover:brightness-110 active:scale-[0.98] transition"
                    style={{ borderColor: '#39FF1455', background: 'rgba(57,255,20,0.10)' }}
                    data-testid="home-slots-in"
                    title="Total transferred INTO Slots today (Central Time)"
                >
                    <Cpu className="w-3.5 h-3.5 text-[#39FF14]" />
                    <span className="text-[10px] font-bold tracking-wider text-[#A0AAB2]">SLOTS IN</span>
                    <span className="text-sm font-black tabular-nums text-[#39FF14]">
                        {snapshot.loading || snapshot.slotsIn == null ? '…' : fmt$compact(snapshot.slotsIn)}
                    </span>
                </button>

                {/* Tables WAGERED today — blackjack + craps + roulette + 3-card poker combined */}
                <button
                    type="button"
                    onClick={() => onDrilldown?.('tables')}
                    className="flex items-center gap-2 rounded-full border px-3 py-1.5 hover:brightness-110 active:scale-[0.98] transition"
                    style={{ borderColor: '#00E5FF55', background: 'rgba(0,229,255,0.10)' }}
                    data-testid="home-tables-wagered"
                    title="Total wagered on Blackjack + Craps + Roulette + 3-Card Poker today"
                >
                    <Dice5 className="w-3.5 h-3.5 text-[#00E5FF]" />
                    <span className="text-[10px] font-bold tracking-wider text-[#A0AAB2]">TABLES</span>
                    <span className="text-sm font-black tabular-nums text-[#00E5FF]">
                        {snapshot.loading || snapshot.tablesWagered == null ? '…' : fmt$compact(snapshot.tablesWagered)}
                    </span>
                </button>

                {/* Lotto WAGERED today */}
                <button
                    type="button"
                    onClick={() => onDrilldown?.('lotto')}
                    className="flex items-center gap-2 rounded-full border px-3 py-1.5 hover:brightness-110 active:scale-[0.98] transition"
                    style={{ borderColor: '#FF4DB855', background: 'rgba(255,77,184,0.10)' }}
                    data-testid="home-lotto-wagered"
                    title="Total wagered on Lotto today"
                >
                    <Ticket className="w-3.5 h-3.5 text-[#FF4DB8]" />
                    <span className="text-[10px] font-bold tracking-wider text-[#A0AAB2]">LOTTO</span>
                    <span className="text-sm font-black tabular-nums text-[#FF4DB8]">
                        {snapshot.loading || snapshot.lottoWagered == null ? '…' : fmt$compact(snapshot.lottoWagered)}
                    </span>
                </button>

                {/* Cashout Fees collected THIS WEEK — rebated back to players on Monday.
                    Tiny operator visibility into the rebate pool building up. */}
                <button
                    type="button"
                    onClick={() => onDrilldown?.('cashout-fees')}
                    className="flex items-center gap-2 rounded-full border px-3 py-1.5 hover:brightness-110 active:scale-[0.98] transition"
                    style={{ borderColor: '#00FFFF55', background: 'rgba(0,255,255,0.10)' }}
                    data-testid="home-fees-collected"
                    title={`Fees charged on this week's cashout requests (6%), the basis for Monday's free-play rebate. Fees retained in the Money summary count only fees on cash actually paid. ${snapshot.feeCashouts || 0} cashout${snapshot.feeCashouts === 1 ? '' : 's'}.`}
                >
                    <Receipt className="w-3.5 h-3.5 text-[#00FFFF]" />
                    <span className="text-[10px] font-bold tracking-wider text-[#A0AAB2]">WEEK FEES (REBATE BASIS)</span>
                    <span className="text-sm font-black tabular-nums text-[#00FFFF]">
                        {snapshot.loading || snapshot.feesCollected == null ? '…' : fmt$compact(snapshot.feesCollected)}
                    </span>
                    {snapshot.feeCashouts > 0 && (
                        <span className="text-[10px] font-bold text-[#A0AAB2]">
                            · {snapshot.feeCashouts}
                        </span>
                    )}
                </button>
            </div>

            {/* Pending signup approvals removed while self-signup is enabled
                (restore when the "approve new customers" toggle is turned on). */}

            {/* Cashier rebate-digest preview — auto-hides when nothing accrued this week. */}
            <CashierDigestPreviewPanel />

            {/* Pending cash-outs — HOME PAGE ONLY (the players/home view). Not
                shown on other admin sections; also reachable via cash-out button. */}
            {isHome && <FinanceSummaryPanel />}

            {isHome && <PendingPayoutsPanel />}

            {isHome && <BuildStamp />}

            {/* Single-click customer panel: full info + edit + actions. */}
            <CustomerPanel
                user={panelUser}
                onClose={() => setPanelUser(null)}
                onOpenCredit={onOpenCredit}
                onOpenFull={onOpenFull}
                onChanged={loadSnapshot}
            />
        </div>
    );
};

export default HomeDashboard;
